import { useContext, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { SnackbarContext } from '../contexts';

import {
  getSelectorValue,
  clientContextWarn,
  hasGraphQLError,
  isUnauthorizedNetworkError,
  redirectLogin,
} from '../utils';
import config from '../config';

const useEnhancedQuery = (query, props = {
  options: {},
  defaultData: {},
  selector: () => {},
  disableDefaultError: false,
}) => {
  const snackbarContext = useContext(SnackbarContext);
  const { setOpenSnackbar } = snackbarContext.actions;
  const history = useHistory();

  const handleOnError = (err) => {
    // UnauthorizedException
    if (isUnauthorizedNetworkError(err)) {
      window.location.href = `${config.app.secureUrl}${redirectLogin()}`;
      return;
    }

    if (!props.disableDefaultError) {
      if (hasGraphQLError(err, 'forbidden')) {
        history.push('/forbidden');
        return;
      }
      if (hasGraphQLError(err, 'item not found')) {
        history.push('/not_found');
        return;
      }
      if (hasGraphQLError(err, 'item has been deleted')) {
        history.push('/item_deleted');
        return;
      }
      setOpenSnackbar({
        variant: 'error',
        message: 'An error has occured. Please try again.',
      });
      clientContextWarn(err);
    }
    if (props.options && props.options.onError) {
      props.options.onError(err);
    }
  };

  const result = useQuery(query, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    ...props.options,
    // Must come after the spread: a caller-supplied options.onError is invoked from inside
    // handleOnError (above), so letting it replace the handler here would skip the 401 redirect
    // and the forbidden / not-found routing for that caller.
    onError: handleOnError,
  });

  // Callers pass defaultData/selector inline, so recomputing data on every render
  // produces a new object each time while the query has no result yet. Effects and
  // callbacks that depend on `data` would then re-run on every render, which can
  // turn into an infinite render loop. Keep data referentially stable until the
  // query result actually changes.
  const defaultDataRef = useRef(props.defaultData);
  const selectorRef = useRef(props.selector);
  selectorRef.current = props.selector;

  const newData = useMemo(
    () => getSelectorValue({ ...defaultDataRef.current, ...result.data }, selectorRef.current),
    [result.data],
  );
  return {
    ...result,
    data: newData,
  };
};

export default useEnhancedQuery;
